import { MailIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SimpleTooltip } from "@/components/ui/primitives";
import { ComposeEmailDialog } from "./ComposeEmailDialog";
import { draftSubjectOf, useMyEmailDrafts } from "./api";
import { EmailDraftsContext, useEmailDrafts } from "./context";

/**
 * Client emails my actions create wait as drafts until I review and send them. A draft that appears while I work
 * (I closed a ticket, sent an invoice) opens in the email form straight away; ones I kept for later are listed
 * under the envelope in the top bar.
 */
export function EmailDraftsProvider({ children }: { children: ReactNode }) {
  const drafts = useMyEmailDrafts();
  const [openId, setOpenId] = useState<string | null>(null);
  const known = useRef<Set<string> | null>(null);
  const queue = useRef<string[]>([]);

  useEffect(() => {
    if (!drafts.data) return;
    const ids = drafts.data.map((d) => d.id);
    if (known.current === null) {
      // Drafts already waiting when the app opened stay under the envelope; only new ones open by themselves.
      known.current = new Set(ids);
      return;
    }
    const fresh = ids.filter((id) => !known.current!.has(id));
    for (const id of fresh) known.current.add(id);
    queue.current.push(...fresh.filter((id) => id !== openId && !queue.current.includes(id)));
    if (!openId && queue.current.length) setOpenId(queue.current.shift() ?? null);
  }, [drafts.data, openId]);

  const openDraft = useCallback((id: string) => {
    known.current?.add(id);
    setOpenId(id);
  }, []);

  const close = useCallback(() => {
    // Several drafts at once (e.g. a bulk edit closed several tickets): show the next one.
    const waiting = new Set((drafts.data ?? []).map((d) => d.id));
    while (queue.current.length && !waiting.has(queue.current[0]!)) queue.current.shift();
    setOpenId(queue.current.shift() ?? null);
  }, [drafts.data]);

  const value = useMemo(() => ({ openDraft }), [openDraft]);
  return (
    <EmailDraftsContext.Provider value={value}>
      {children}
      <ComposeEmailDialog draftId={openId} onClose={close} />
    </EmailDraftsContext.Provider>
  );
}

/** Top-bar envelope: emails waiting for me to review and send. Hidden when there are none. */
export function EmailDraftsButton() {
  const { data } = useMyEmailDrafts();
  const { openDraft } = useEmailDrafts();
  const count = data?.length ?? 0;
  if (!count) return null;
  const label = `${count} ${count === 1 ? "email" : "emails"} waiting to be sent`;
  return (
    <DropdownMenu>
      <SimpleTooltip label="Emails to review">
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={label} className="relative">
            <MailIcon />
            <span aria-hidden className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-warning px-1 text-[10px] leading-none font-semibold text-warning-foreground tabular-nums">
              {count > 99 ? "99+" : count}
            </span>
          </Button>
        </DropdownMenuTrigger>
      </SimpleTooltip>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Emails to review and send</DropdownMenuLabel>
        {data!.map((d) => (
          <DropdownMenuItem key={d.id} onSelect={() => openDraft(d.id)} className="flex flex-col items-start gap-0.5">
            <span className="w-full truncate text-sm font-medium">{d.subject}</span>
            <span className="w-full truncate text-xs text-muted-foreground">
              {draftSubjectOf(d.entity)} · to {d.to.join(", ") || "nobody yet"}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
